<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import AipayOKSupportAPI from 'dashboard/api/aipayokSupport';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const emit = defineEmits(['conversationOpened']);
const router = useRouter();
const { t, te } = useI18n();
const query = ref('');
const results = ref([]);
const isLoading = ref(false);
const hasSearched = ref(false);
const hasError = ref(false);
const selectedResult = ref(null);
const proactiveMessage = ref('');
const proactiveRequestId = ref('');
const proactiveError = ref('');
const isSending = ref(false);

const translateValue = (group, value) => {
  if (!value) return '';

  const key = `CONVERSATION_SIDEBAR.AIPAYOK.${group}.${String(value).toUpperCase()}`;
  // Status keys come from the trusted AipayOK API enum values.
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return te(key) ? t(key) : t('CONVERSATION_SIDEBAR.AIPAYOK.UNKNOWN_STATUS');
};

const formatDate = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const search = async () => {
  const normalizedQuery = query.value.trim();
  if (normalizedQuery.length < 2 || isLoading.value) return;

  isLoading.value = true;
  hasError.value = false;
  hasSearched.value = true;
  try {
    const { data } = await AipayOKSupportAPI.search(normalizedQuery);
    results.value = data.payload || [];
  } catch {
    results.value = [];
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const openResult = result => {
  const accountId = router.currentRoute.value.params.accountId;
  if (result.conversationId) {
    emit('conversationOpened');
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId,
        conversation_id: result.conversationId,
      },
    });
    return;
  }

  if (result.contactId) {
    emit('conversationOpened');
    router.push({
      name: 'contacts_edit',
      params: { accountId, contactId: result.contactId },
    });
    return;
  }

  if (result.userUrl) {
    window.open(result.userUrl, '_blank', 'noopener,noreferrer');
  }
};

const startProactiveContact = result => {
  selectedResult.value = result;
  proactiveMessage.value = '';
  proactiveError.value = '';
  proactiveRequestId.value = crypto.randomUUID();
};

const cancelProactiveContact = () => {
  selectedResult.value = null;
  proactiveMessage.value = '';
  proactiveRequestId.value = '';
  proactiveError.value = '';
};

const sendProactiveMessage = async () => {
  const message = proactiveMessage.value.trim();
  if (!selectedResult.value || !message || isSending.value) return;

  isSending.value = true;
  proactiveError.value = '';
  try {
    const { data } = await AipayOKSupportAPI.proactiveContact({
      user_id: selectedResult.value.userId,
      message,
      request_id: proactiveRequestId.value,
    });
    const accountId = router.currentRoute.value.params.accountId;
    cancelProactiveContact();
    emit('conversationOpened');
    await router.push({
      name: 'inbox_conversation',
      params: {
        accountId,
        conversation_id: data.conversationId,
      },
    });
  } catch (error) {
    proactiveError.value =
      error.response?.data?.error ||
      t('CONVERSATION_SIDEBAR.AIPAYOK.PROACTIVE_ERROR');
  } finally {
    isSending.value = false;
  }
};
</script>

<template>
  <section class="mx-2 mb-3 rounded-lg border border-n-weak bg-n-surface-1 p-3">
    <h3 class="mb-2 text-sm font-medium text-n-slate-12">
      {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.SEARCH_TITLE') }}
    </h3>
    <form class="flex gap-2" @submit.prevent="search">
      <input
        v-model="query"
        type="search"
        class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-2 text-xs text-n-slate-12 outline-none focus:border-n-brand"
        :placeholder="$t('CONVERSATION_SIDEBAR.AIPAYOK.SEARCH_PLACEHOLDER')"
      />
      <button
        type="submit"
        class="rounded-lg bg-n-brand px-3 py-2 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="query.trim().length < 2 || isLoading"
      >
        {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.SEARCH') }}
      </button>
    </form>

    <div v-if="isLoading" class="flex justify-center py-3">
      <Spinner />
    </div>
    <p v-else-if="hasError" class="mt-2 text-xs text-n-ruby-10">
      {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.SEARCH_ERROR') }}
    </p>
    <p
      v-else-if="hasSearched && !results.length"
      class="mt-2 text-xs text-n-slate-10"
    >
      {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.SEARCH_EMPTY') }}
    </p>
    <div v-else-if="results.length" class="mt-2 flex flex-col gap-2">
      <div
        v-for="result in results"
        :key="result.userId"
        class="rounded-lg bg-n-alpha-2 p-2.5"
      >
        <button
          type="button"
          class="flex w-full items-start justify-between gap-2 text-left"
          data-test="open-customer-result"
          @click="openResult(result)"
        >
          <span class="min-w-0">
            <span class="block truncate text-xs font-medium text-n-slate-12">
              {{ result.email }}
            </span>
            <span class="block truncate text-xs text-n-slate-10">
              {{
                $t('CONVERSATION_SIDEBAR.AIPAYOK.USER_ID', {
                  id: result.userId,
                })
              }}
              <template v-if="result.matchedOrder">
                {{
                  $t('CONVERSATION_SIDEBAR.AIPAYOK.MATCHED_ORDER', {
                    orderNo: result.matchedOrder.orderNo,
                  })
                }}
              </template>
            </span>
            <span
              v-if="result.matchedOrder?.planName"
              class="block truncate text-xs text-n-slate-10"
            >
              {{ result.matchedOrder.planName }}
              <template v-if="result.matchedOrder.status">
                {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.DETAIL_SEPARATOR') }}
                {{ translateValue('ORDER_STATUS', result.matchedOrder.status) }}
              </template>
            </span>
            <span class="block truncate text-xs text-n-slate-9">
              <template v-if="result.status">
                {{ translateValue('ACCOUNT_STATUS', result.status) }}
              </template>
              <template v-if="formatDate(result.registeredAt)">
                {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.DETAIL_SEPARATOR') }}
                {{
                  $t('CONVERSATION_SIDEBAR.AIPAYOK.REGISTERED_DATE', {
                    date: formatDate(result.registeredAt),
                  })
                }}
              </template>
            </span>
            <span
              v-if="!result.conversationId && !result.contactId"
              class="block truncate text-xs text-n-amber-10"
            >
              {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.NO_SUPPORT_HISTORY') }}
            </span>
          </span>
          <span
            v-if="result.identityVerified"
            class="shrink-0 rounded-full bg-n-teal-3 px-2 py-0.5 text-xs text-n-teal-11"
          >
            {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.VERIFIED') }}
          </span>
          <span v-else class="i-lucide-search size-4 shrink-0 text-n-slate-9" />
        </button>

        <button
          type="button"
          class="mt-2 flex items-center gap-1 text-xs font-medium text-n-brand hover:underline"
          data-test="start-proactive-contact"
          @click="startProactiveContact(result)"
        >
          <span class="i-lucide-message-square-plus size-3.5" />
          {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.PROACTIVE_CONTACT') }}
        </button>

        <form
          v-if="selectedResult?.userId === result.userId"
          class="mt-2 space-y-2 border-t border-n-weak pt-2"
          data-test="proactive-contact-form"
          @submit.prevent="sendProactiveMessage"
        >
          <p class="text-xs text-n-slate-10">
            {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.PROACTIVE_OFFLINE_NOTE') }}
          </p>
          <textarea
            v-model="proactiveMessage"
            rows="3"
            maxlength="2000"
            class="w-full resize-y rounded-lg border border-n-weak bg-n-alpha-1 px-2.5 py-2 text-xs text-n-slate-12 outline-none focus:border-n-brand"
            :placeholder="
              $t('CONVERSATION_SIDEBAR.AIPAYOK.PROACTIVE_PLACEHOLDER')
            "
          />
          <p v-if="proactiveError" class="text-xs text-n-ruby-10">
            {{ proactiveError }}
          </p>
          <div class="flex justify-end gap-2">
            <button
              type="button"
              class="rounded-lg px-2.5 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-3"
              :disabled="isSending"
              @click="cancelProactiveContact"
            >
              {{ $t('CONVERSATION_SIDEBAR.AIPAYOK.PROACTIVE_CANCEL') }}
            </button>
            <button
              type="submit"
              class="rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="!proactiveMessage.trim() || isSending"
              data-test="send-proactive-message"
            >
              {{
                isSending
                  ? $t('CONVERSATION_SIDEBAR.AIPAYOK.PROACTIVE_SENDING')
                  : $t('CONVERSATION_SIDEBAR.AIPAYOK.PROACTIVE_SEND')
              }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>
